import tinycolor from 'tinycolor2';
import {colorMixKM, getLookUpTable} from './kubelkaMonk';
import {LRUStrings, LRUStringNode, Point2D, SeededRandom} from './utils';
import {ChangeTextureSet, FingerTip, FlowCell, FlowCellMove, TipApplication} from './fingertip';
import { CrcOff, Page } from './page';
import { initialize } from 'workbox-google-analytics';
import { TouchPageChange, TouchProgramInfo } from './TouchPageChange';
import { CirclePageChange } from './circlePageChange';
import { DryPageChange } from './dryPageChange';
import { BlowPageChange } from './blowPageChange';
import { RefreshPageChange } from './refreshPageChange';
import { PageChange } from './PageChange';
import { SmearPageChange } from './smearPageChange';
import smearGenerator from './smearGen';
import { KnifePageChange } from './knifePageChange';
import { SamplePageChange } from './samplePageChange';
import { StampPageChange } from './stampPageChange';

export interface UniformLocations {
    resolution: WebGLUniformLocation,
};

export interface ProgramInfo {
    tag: string,
    program: WebGLProgram,
    attribLocations: {
        vertexPosition: GLint,
        texCoordLocation: GLint,
    },
    uniformLocations: UniformLocations,
};

export interface ProgramBuffers {
    vertexPositionBuffer: WebGLBuffer,
    texCoordBuffer: WebGLBuffer
};

export class GPUChecker {
    ctxTag: string;
    ctxLoc: string;
    public gl: WebGL2RenderingContext;
    static healthy: boolean = true;
    static lostContext: boolean = false;
    static errorCode: number = 0;
    static errorLoc: string = '';
    static errorText: string = '';
    static errorChecks: Set<string> = new Set<string>();
    static reset() {
        GPUChecker.healthy = true;
        GPUChecker.lostContext = false;
        GPUChecker.errorCode = 0;
        GPUChecker.errorLoc = '';
        GPUChecker.errorText = '';
        GPUChecker.errorChecks.clear();
    }
    constructor(gl: WebGLRenderingContext, prev: GPUChecker | undefined, ctx: string) {
        if (prev === undefined) {
            this.ctxTag = ctx;
        } else {
            this.ctxTag = `${prev.ctxTag}@${prev.ctxLoc}>${ctx}`;
        }
        this.ctxLoc = '';
        this.gl = gl as WebGL2RenderingContext;
    }

    public check(loc: string, intFunc: ()=>unknown ): unknown {
        if (GPUChecker.healthy) {
            this.ctxLoc = loc;
            return intFunc();
        }
        return null;
    }
    public checkAPI(loc: string, intFunc: ()=>unknown, nullIsError:boolean = false ): unknown {
        if (GPUChecker.healthy) {
            let locTag = `${this.ctxTag}@${loc}`
            let res = intFunc();
            if (this.gl!.isContextLost()) {
                GPUChecker.healthy = false;
                GPUChecker.errorCode = 0;
                GPUChecker.lostContext = true;
                GPUChecker.errorLoc = locTag;
            } else if (res === null && nullIsError) {
                GPUChecker.healthy = false;
                GPUChecker.errorCode = this.gl!.getError();
                if (GPUChecker.errorCode === 0) {
                    GPUChecker.errorCode = 0x0508;
                }
                GPUChecker.errorLoc = locTag;
                GPUChecker.errorText = `tbd`;
            } else if (!GPUChecker.errorChecks.has(locTag)) {
                GPUChecker.errorChecks.add(locTag);
                GPUChecker.errorCode = this.gl!.getError();
                if (GPUChecker.errorCode !== 0) {
                    GPUChecker.healthy = false;
                    GPUChecker.errorLoc = locTag;
                    GPUChecker.errorText = `tbd`;
                }
            }
            return res;
        }
        return null;
    }
    public checkFB(loc: string): void {
        if (GPUChecker.healthy) {
            let locTag = `${this.ctxTag}@${loc}`
            if (this.gl!.isContextLost()) {
                GPUChecker.healthy = false;
                GPUChecker.errorCode = 0;
                GPUChecker.lostContext = true;
                GPUChecker.errorLoc = locTag;
            } else if (!GPUChecker.errorChecks.has(locTag)) {
                GPUChecker.errorChecks.add(locTag);
                let fbCode = this.gl!.checkFramebufferStatus(this.gl!.FRAMEBUFFER);
                if (fbCode !== this.gl.FRAMEBUFFER_COMPLETE) {
                    GPUChecker.healthy = false;
                    GPUChecker.errorLoc = locTag;
                    GPUChecker.errorCode = fbCode;
                    switch (fbCode) {
                        case this.gl.FRAMEBUFFER_INCOMPLETE_ATTACHMENT:
                            GPUChecker.errorText = `FB Check: incomplete attachment`;
                            break;
                        case this.gl.FRAMEBUFFER_INCOMPLETE_DIMENSIONS:
                            GPUChecker.errorText = 'FB Check: incomplete dimensions';
                            break;  
                        case this.gl.FRAMEBUFFER_INCOMPLETE_MISSING_ATTACHMENT:
                            let aDet = '';
                            for(let a=0; a<4; a++) {
                                let aType = this.gl!.getFramebufferAttachmentParameter(this.gl!.FRAMEBUFFER, this.gl!.COLOR_ATTACHMENT0 + a, this.gl!.FRAMEBUFFER_ATTACHMENT_COMPONENT_TYPE);
                                aDet += `${a}=(${aType},`;
                                let aRed = this.gl!.getFramebufferAttachmentParameter(this.gl!.FRAMEBUFFER, this.gl!.COLOR_ATTACHMENT0 + a, this.gl!.FRAMEBUFFER_ATTACHMENT_RED_SIZE);
                                aDet += `${aRed},`;
                                let aGreen = this.gl!.getFramebufferAttachmentParameter(this.gl!.FRAMEBUFFER, this.gl!.COLOR_ATTACHMENT0 + a, this.gl!.FRAMEBUFFER_ATTACHMENT_GREEN_SIZE);
                                aDet += `${aGreen},`;
                                let aBlue = this.gl!.getFramebufferAttachmentParameter(this.gl!.FRAMEBUFFER, this.gl!.COLOR_ATTACHMENT0 + a, this.gl!.FRAMEBUFFER_ATTACHMENT_BLUE_SIZE);
                                aDet += `${aBlue},`;
                                let aAlpha = this.gl!.getFramebufferAttachmentParameter(this.gl!.FRAMEBUFFER, this.gl!.COLOR_ATTACHMENT0 + a, this.gl!.FRAMEBUFFER_ATTACHMENT_ALPHA_SIZE);
                                aDet += `${aAlpha}); `;

                            }
                            GPUChecker.errorText = `FB Check: incomplete missing attachment [${aDet}]`;
                            break;
                        case this.gl.FRAMEBUFFER_UNSUPPORTED:
                            GPUChecker.errorText = 'FB Check: unsupported';
                            break;
                        case this.gl.FRAMEBUFFER_INCOMPLETE_MULTISAMPLE:
                            GPUChecker.errorText = 'FB Check: incomplete multisample';
                            break;
                        
                    }
                }
            }
        }
    }

    public setError(loc: string, code: number, text: string) {
        GPUChecker.healthy = false;
        if (this.gl && this.gl!.isContextLost()) {
            GPUChecker.lostContext = true;
        } else {
            GPUChecker.errorCode = code;
            GPUChecker.errorLoc = `${this.ctxTag}@${loc}`;
            GPUChecker.errorText = text;
        }
    }
}

export interface WorkingItem {
    fb: WebGLFramebuffer | null;
    tex0: WebGLTexture | null;
    tex1: WebGLTexture | null;
    tex2: WebGLTexture | null;
    tex3: WebGLTexture | null;
}

export class WorkingFrameFactory {
    maxItems: number = 1000;
    currentItems: number = 0;
    usedItems: Map<string, Array<WorkingItem>> = new Map<string, Array<WorkingItem>>();
    cachedItems: Map<string, Array<[LRUStringNode, WorkingItem]>> = new Map<string, Array<[LRUStringNode, WorkingItem]>>();
    cachedLRU: LRUStrings = new LRUStrings();
    activeTextures: Map<number, WebGLTexture | null> = new Map<number, WebGLTexture | null>();
    makeKey(frameBuffer: boolean, width: number, height: number, texShapes: Array<{internalFormat: GLenum, format: GLenum, type: GLenum}>): string {
        let key = `${frameBuffer ? 'fb' : 'tex'}-${width}-${height}-`;
        return key + texShapes.map((ts) => `${ts.internalFormat}-${ts.format}-${ts.type}`).join(';');
    }
    clearUsage(gc: GPUChecker) {
        // put used items on top of cache and end of list for cached items
        for (const usedPair of this.usedItems) {
            let key = usedPair[0];
            for (const usedItem of usedPair[1]) {
                let node = this.cachedLRU.addFirst(key)
                this.cachedItems.get(key)!.push([node, usedItem]);
                if (usedItem.fb) {
                    gc.gl.bindFramebuffer(gc.gl.FRAMEBUFFER, usedItem.fb);
                    gc.gl.invalidateFramebuffer(gc.gl.FRAMEBUFFER, [gc.gl.COLOR_ATTACHMENT0, gc.gl.COLOR_ATTACHMENT1, gc.gl.COLOR_ATTACHMENT2, gc.gl.COLOR_ATTACHMENT3]);
                }
            }
        }

        // for(const activeTexPair of this.activeTextures) {
        //     if (activeTexPair[1] !== null) {
        //         gc.gl.activeTexture(activeTexPair[0]);
        //         gc.gl.bindTexture(gc.gl.TEXTURE_2D, null);
        //     }
        // }

        this.usedItems.clear();
        this.activeTextures.clear();
    }

    setActivateTexture(idx: number, tex: WebGLTexture | null) {
        this.activeTextures.set(idx, tex);
    }

    removeOldest(gc: GPUChecker) {
        let key = this.cachedLRU.removeLast();
        let cachedL = this.cachedItems.get(key);
        if (cachedL && cachedL.length > 0) {
            let wi = cachedL.splice(0, 1)[0][1];
            if (wi.fb) {
                gc.gl.deleteFramebuffer(wi.fb);
                this.currentItems--;
            }
            if (wi.tex0) {
                gc.gl.deleteTexture(wi.tex0);
                this.currentItems--;
            }
            if (wi.tex1) {
                gc.gl.deleteTexture(wi.tex1);
                this.currentItems--;
            }
            if (wi.tex2) {
                gc.gl.deleteTexture(wi.tex2);
                this.currentItems--;
            }
            if (wi.tex3) {
                gc.gl.deleteTexture(wi.tex3);
                this.currentItems--;
            }

        }
    }

    getItem(gcFrom: GPUChecker, fbTag: string | undefined, width: number, height: number, texShapes: Array<{tag:string, internalFormat: GLenum, format: GLenum, type: GLenum}>) : WorkingItem {
        const gc = new GPUChecker(gcFrom.gl, gcFrom, 'getWorkingItem');
        const gl = gc.gl;

        const texTag = (idx: number) => {
            return `${fbTag!==undefined ? fbTag+'-': ''}${texShapes[idx].tag}`
        }
        let key = this.makeKey(fbTag !== undefined, width, height, texShapes);
        let cachedL = this.cachedItems.get(key);
        if (cachedL && cachedL.length > 0) {
            // taken from the cache. doesn't change overall size of set. add to used and remove from cached
            let [node, item] = cachedL.pop()!;
            let usedL = this.usedItems.get(key);
            if (!usedL) {
                usedL = new Array<WorkingItem>();
                this.usedItems.set(key, usedL);
            }
            usedL.push(item);
            this.cachedLRU.removeAny(node);
            if (item.fb) {
                let old = (item.fb as any).tag;
                (item.fb as any).tag = fbTag;
                //console.log(`reusing fb ${old} as ${fbTag}`);
            }
            if (item.tex0) {
                let old = (item.tex0 as any).tag;
                (item.tex0 as any).tag = texTag(0);
                //console.log(`reusing tex ${old} as ${(item.tex0 as any).tag}`);
            }
            if (item.tex1) {
                let old = (item.tex1 as any).tag;
                (item.tex1 as any).tag = texTag(1);
                //console.log(`reusing tex ${old} as ${(item.tex1 as any).tag}`);
            }
            if (item.tex2) {
                let old = (item.tex2 as any).tag;
                (item.tex2 as any).tag = texTag(2);
                //console.log(`reusing tex ${old} as ${(item.tex2 as any).tag}`);
            }
            if (item.tex3) {
                let old = (item.tex3 as any).tag;
                (item.tex3 as any).tag = texTag(3);
                //console.log(`reusing tex ${old} as ${(item.tex3 as any).tag}`);
            }
            return item;
        } else {
            if (this.currentItems >= this.maxItems) {
                this.removeOldest(gc);
            }
            let newItem: WorkingItem = {fb: null, tex0: null, tex1: null, tex2: null, tex3: null};
            if (texShapes.length > 0) {
                newItem.tex0 = gc.check('1', ()=>GPURunner.initTexture(gc, texTag(0))) as WebGLTexture;
                this.currentItems++;
                gc.checkAPI('2', ()=>gl.bindTexture(gl.TEXTURE_2D, newItem.tex0));
                gc.checkAPI('3', ()=>gl.texImage2D(gl.TEXTURE_2D, 0, texShapes[0].internalFormat, width, height, 0, texShapes[0].format, texShapes[0].type, null));
            }
            if (texShapes.length > 1) {
                newItem.tex1 = gc.check('4', ()=>GPURunner.initTexture(gc, texTag(1))) as WebGLTexture;
                this.currentItems++;
                gc.checkAPI('5', ()=>gl.bindTexture(gl.TEXTURE_2D, newItem.tex1));
                gc.checkAPI('6', ()=>gl.texImage2D(gl.TEXTURE_2D, 0, texShapes[1].internalFormat, width, height, 0, texShapes[1].format, texShapes[1].type, null));
            }
            if (texShapes.length > 2) {
                newItem.tex2 = gc.check('7', ()=>GPURunner.initTexture(gc, texTag(2))) as WebGLTexture;
                this.currentItems++;
                gc.checkAPI('8', ()=>gl.bindTexture(gl.TEXTURE_2D, newItem.tex2));
                gc.checkAPI('9', ()=>gl.texImage2D(gl.TEXTURE_2D, 0, texShapes[2].internalFormat, width, height, 0, texShapes[2].format, texShapes[2].type, null));
            }
            if (texShapes.length > 3) {
                newItem.tex3 = gc.check('10', ()=>GPURunner.initTexture(gc, texTag(3))) as WebGLTexture;
                this.currentItems++;
                gc.checkAPI('11', ()=>gl.bindTexture(gl.TEXTURE_2D, newItem.tex3));
                gc.checkAPI('12', ()=>gl.texImage2D(gl.TEXTURE_2D, 0, texShapes[3].internalFormat, width, height, 0, texShapes[3].format, texShapes[3].type, null));
            }
            if (fbTag !== undefined) {
                newItem.fb = gc.check('13', ()=>GPURunner.initTextureFramebuffer(gc, fbTag, newItem.tex0!, newItem.tex1!, newItem.tex2!, newItem.tex3!)) as WebGLFramebuffer;
                this.currentItems++;
            }

            if (!cachedL) {
                cachedL = new Array<[LRUStringNode, WorkingItem]>();
                this.cachedItems.set(key, cachedL);
            }
            
            let usedL = this.usedItems.get(key);
            if (!usedL) {
                usedL = new Array<WorkingItem>();
                this.usedItems.set(key, usedL);
            }
            usedL.push(newItem);
            return newItem;
        }
    }

}

export class GPURunner {
    constructor() {
    }

    static gl: WebGL2RenderingContext | undefined;
    static initializedPrograms: Map<ProgramInfo, Set<string>> ;
    static workingFrameFactory: WorkingFrameFactory;

    static newChecker(ctx: string) {
        return new GPUChecker(GPURunner.gl!, undefined, ctx);
    }
        
    static initializeGPU(textureCallback: (radius: number, application: TipApplication) => ChangeTextureSet) {
        if (!GPURunner.gl) {
            console.time('initializing GPU: done');
            console.log('initializing GPU');
            let cvs = document.createElement('canvas');
            
            const gl = cvs.getContext("webgl2", {antialias: false, powerPreference:'high-performance', failIfMajorPerformanceCaveat:true})!;
            if (!gl) {
                let gc = GPURunner.newChecker('init');
                gc.setError('GPU Check', -1, `WebGL2 is not supported on this device`);
                return;
            }
            GPURunner.gl = gl;
            let gc = GPURunner.newChecker('init');

            gl.canvas.addEventListener('webglcontextlost', (event) => {
                event.preventDefault(); 
                GPUChecker.healthy = false; 
                GPUChecker.lostContext = true;
               console.log('context lost');
            });
            gl.canvas.addEventListener('webglcontextrestored', (event) => {
                console.log('context restored');
                GPUChecker.reset();
                GPURunner.initializeGPU(textureCallback);
            });

            // check min capabilities
            let maxTexSize = gl.getParameter(gl.MAX_TEXTURE_SIZE);
            if (maxTexSize < 1024) {
                gc.setError('GPU Check', gl.INVALID_OPERATION, `Max texture size too small: ${maxTexSize}, Smesshy needs at least 1024`);
                return;
            }
            let maxActiveTextures = gl.getParameter(gl.MAX_TEXTURE_IMAGE_UNITS);
            if (maxActiveTextures < 16) {
                gc.setError('GPU Check', gl.INVALID_OPERATION, `Max active textures too small: ${maxActiveTextures}, Smesshy needs at least 16`);
                return;
            }
            let maxDrawBuffers = gl.getParameter(gl.MAX_DRAW_BUFFERS);
            if (maxDrawBuffers < 4) {
                gc.setError('GPU Check', gl.INVALID_OPERATION, `Max draw buffers too small: ${maxDrawBuffers}, Smesshy needs at least 4`);
                return;
            }
            let maxColorAttachments = gl.getParameter(gl.MAX_COLOR_ATTACHMENTS);
            if (maxColorAttachments < 4) {
                gc.setError('GPU Check', gl.INVALID_OPERATION, `Max color attachments too small: ${maxColorAttachments}, Smesshy needs at least 4`);
                return;
            }
            let maxTextureArrayLayers = gl.getParameter(gl.MAX_ARRAY_TEXTURE_LAYERS);
            if (maxTextureArrayLayers < 90) {
                gc.setError('GPU Check', gl.INVALID_OPERATION, `Max texture array layers too small: ${maxTextureArrayLayers}, Smesshy needs at least 90`);
                return;
            }
            let prec = gl.getShaderPrecisionFormat(gl.FRAGMENT_SHADER, gl.HIGH_FLOAT);
            if (prec!.precision < 23) {
                gc.setError('GPU Check', gl.INVALID_OPERATION, `High float precision too small: ${prec!.precision}, Smesshy needs at least 23`);
                return;
            }
             prec = gl.getShaderPrecisionFormat(gl.FRAGMENT_SHADER, gl.HIGH_INT);
            if (prec!.rangeMax < 30) {
                gc.setError('GPU Check', gl.INVALID_OPERATION, `High int precision too small: ${prec!.rangeMax}, Smesshy needs at least 30`);
                return;
            }
            // let crt = gl.getParameter(gl.IMPLEMENTATION_COLOR_READ_FORMAT);
            // //gc.setError('GPU Check', gl.INVALID_OPERATION, `Implementation color read format: ${crt}`);
            // //return;
            // let cdt = gl.getParameter(gl.IMPLEMENTATION_COLOR_READ_TYPE);
            // gc.setError('GPU Check', gl.INVALID_OPERATION, `Implementation color read format: ${cdt}`);
            // return;


            GPURunner.initializedPrograms = new Map<ProgramInfo, Set<string>>();
            //let dbg = gl.getExtension('WEBGL_debug_shaders');

            // load vertex and fragment shaders. compile and link
            const vertexShader = gc.checkAPI('loadShader', ()=>GPURunner.loadShader(gc, gl.VERTEX_SHADER, PageChange.vsSource), true) as WebGLShader;
            if (!GPUChecker.healthy) {
                GPUChecker.errorText = `Failed to load vertex shader`;
                return;
            }

            const fragmentShaderPrograms = TouchPageChange.getPrograms().concat(CirclePageChange.getPrograms()).concat(SmearPageChange.getPrograms())
                        .concat(BlowPageChange.getPrograms()).concat(DryPageChange.getPrograms()).concat(RefreshPageChange.getPrograms()).concat(KnifePageChange.getPrograms()
                    .concat(SamplePageChange.getPrograms()).concat(StampPageChange.getPrograms()));

            for (const codeInfo of fragmentShaderPrograms) {
                const shaderProgram = gc.checkAPI('createProgram', ()=>gl.createProgram(), true) as WebGLProgram;
                if (!GPUChecker.healthy) {
                    GPUChecker.errorText = `Failed to create program for ${codeInfo[1].tag} shader`;
                    return;
                }
                const fragmentShader = gc.check('1', ()=>GPURunner.loadShader(gc, gl.FRAGMENT_SHADER, codeInfo[0])) as WebGLShader;
                if (!GPUChecker.healthy) {
                    GPUChecker.errorText = `Failed to load fragment shader for ${codeInfo[1].tag}`;
                    return;
                }

                gl.attachShader(shaderProgram, vertexShader);
                gl.attachShader(shaderProgram, fragmentShader);
                gl.linkProgram(shaderProgram);
    
                //let src = dbg?.getTranslatedShaderSource(fragmentShader);
                (shaderProgram as any).tag = codeInfo[1].tag;
                if (!gl.getProgramParameter(shaderProgram, gl.LINK_STATUS)) {
                    gc.setError('link', gl.INVALID_OPERATION, `Link failed: ${codeInfo[1].tag} ${gl.getProgramInfoLog(shaderProgram)}\n vs info-log: ${gl.getShaderInfoLog(vertexShader)}\n fs info-log: ${gl.getShaderInfoLog(fragmentShader)}`);
                    return;
                }
                codeInfo[1].program = shaderProgram;    
            }


            //let spew = dbg?.getTranslatedShaderSource(fragmentShaderFinish);
            //console.log(spew);

            // get the attribute locations per program
            gc.check('1', ()=>TouchPageChange.initProgramLocations(gc));
            gc.check('2', ()=>CirclePageChange.initProgramLocations(gc));
            gc.check('3', ()=>SmearPageChange.initProgramLocations(gc));
            gc.check('4', ()=>BlowPageChange.initProgramLocations(gc));
            gc.check('5', ()=>DryPageChange.initProgramLocations(gc));
            gc.check('6', ()=>RefreshPageChange.initProgramLocations(gc));
            gc.check('7', ()=>KnifePageChange.initProgramLocations(gc));
            gc.check('8', ()=>SamplePageChange.initProgramLocations(gc));
            gc.check('9', ()=>StampPageChange.initProgramLocations(gc));

            // set up the permanent textures and buffers 
            GPURunner.workingFrameFactory = new WorkingFrameFactory();
            gc.check('20', ()=>PageChange.initPermanentStorage(gc, textureCallback));
            gc.check('21', ()=>TouchPageChange.initPermanentStorage(gc, textureCallback));
            gc.check('22', ()=>CirclePageChange.initPermanentStorage(gc, textureCallback));
            gc.check('23', ()=>SmearPageChange.initPermanentStorage(gc, textureCallback));
            gc.check('24', ()=>BlowPageChange.initPermanentStorage(gc, textureCallback));
            gc.check('25', ()=>DryPageChange.initPermanentStorage(gc, textureCallback));
            gc.check('26', ()=>RefreshPageChange.initPermanentStorage(gc, textureCallback));
            gc.check('27', ()=>KnifePageChange.initPermanentStorage(gc, textureCallback));
            gc.check('28', ()=>SamplePageChange.initPermanentStorage(gc, textureCallback));
            gc.check('29', ()=>StampPageChange.initPermanentStorage(gc, textureCallback));

            // attribute pointers
            gc.checkAPI('bindBuffer1', ()=>gl.bindBuffer(gl.ARRAY_BUFFER, PageChange.vertexBuffers!.texCoordBuffer));
            // all programs share these, so use the first one
            gc.checkAPI('vertexAttribPointer1', ()=>gl.vertexAttribPointer(TouchPageChange.externalToLatentProgram.attribLocations.texCoordLocation, 2, gl.FLOAT, false, 0, 0));
            gc.checkAPI('enableVertexAttribArray1', ()=>gl.enableVertexAttribArray(TouchPageChange.externalToLatentProgram.attribLocations.texCoordLocation));

            gc.checkAPI('bindBuffer2', ()=>gl.bindBuffer(gl.ARRAY_BUFFER, PageChange.vertexBuffers!.vertexPositionBuffer));
            gc.checkAPI('vertexAttribPointer2', ()=>gl.vertexAttribPointer(TouchPageChange.externalToLatentProgram.attribLocations.vertexPosition, 2, gl.FLOAT, false, 0, 0));
            gc.checkAPI('enableVertexAttribArray2', ()=>gl.enableVertexAttribArray(TouchPageChange.externalToLatentProgram.attribLocations.vertexPosition));
    
            gl.flush();
            GPURunner.gl = gl;
            console.timeEnd('initializing GPU: done');
        }
    }

    static activateTexture(gcFrom: GPUChecker, idx: number, tex: WebGLTexture | null) {
        const gc = new GPUChecker(gcFrom.gl, gcFrom, 'activateTexture');
        gc.checkAPI('activate', ()=>gc.gl.activeTexture(gc.gl.TEXTURE0 + idx));
        gc.checkAPI('bind', ()=>gc.gl.bindTexture(gc.gl.TEXTURE_2D, tex));
        GPURunner.workingFrameFactory.setActivateTexture(gc.gl.TEXTURE0 + idx, tex);
    }

    static doInitializeProgram(program: ProgramInfo, fromCtx: string) : Boolean {
        let initCtxSet = GPURunner.initializedPrograms.get(program);
        if (initCtxSet === undefined) {
            initCtxSet = new Set<string>();
            GPURunner.initializedPrograms.set(program, initCtxSet);
        }
        if (initCtxSet.has(fromCtx)) {
            return false;
        }
        initCtxSet.add(fromCtx);
        return true;
    }

    static initTexture(gcFrom: GPUChecker, tag:string, array: boolean = false) : WebGLTexture {
        const gc = new GPUChecker(gcFrom.gl, gcFrom, 'initTexture');
        const gl = gc.gl;
        // create to render to
        const targetTexture = gc.checkAPI('1', ()=>gl.createTexture(), true) as WebGLTexture;
        (targetTexture! as any).tag = tag;
        if (array) {
            gc.checkAPI('2', ()=>gl.bindTexture(gl.TEXTURE_2D_ARRAY, targetTexture));
            // Set the parameters so we can render any size image.
            gl.texParameteri(gl.TEXTURE_2D_ARRAY, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
            gl.texParameteri(gl.TEXTURE_2D_ARRAY, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
            gl.texParameteri(gl.TEXTURE_2D_ARRAY, gl.TEXTURE_MIN_FILTER, gl.NEAREST);
            gl.texParameteri(gl.TEXTURE_2D_ARRAY, gl.TEXTURE_MAG_FILTER, gl.NEAREST);
        }
        else {
            gc.checkAPI('2', ()=>gl.bindTexture(gl.TEXTURE_2D, targetTexture));
            // Set the parameters so we can render any size image.
            gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
            gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
            gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.NEAREST);
            gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.NEAREST);
        }
        return targetTexture!;
    }

    static initTextureFramebuffer(gcFrom: GPUChecker, tag:string, tex0: WebGLTexture, tex1: WebGLTexture | undefined = undefined,
                                    text2: WebGLTexture | undefined = undefined, text3: WebGLTexture | undefined = undefined,
                                    text4: WebGLTexture | undefined = undefined, text5: WebGLTexture | undefined = undefined,
                                    text6: WebGLTexture | undefined = undefined) : WebGLFramebuffer {
        const gc = new GPUChecker(gcFrom.gl, gcFrom, 'initTextureFramebuffer');
        const gl = gc.gl;

        const fb = gc.checkAPI('1', ()=>gl.createFramebuffer(), true) as WebGLFramebuffer;
        (fb! as any).tag = tag;

        gc.checkAPI('2', ()=>gl.bindFramebuffer(gl.FRAMEBUFFER, fb));
        // attach the texture as the first color attachment
        gc.checkAPI('3', ()=>gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, tex0, 0));
        if (tex1) {
            gc.checkAPI('4', ()=>gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT1, gl.TEXTURE_2D, tex1, 0));
            if (text2) {
                gc.checkAPI('5', ()=>gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT2, gl.TEXTURE_2D, text2, 0));
                if (text3) {
                    gc.checkAPI('6', ()=>gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT3, gl.TEXTURE_2D, text3, 0));
                    if (text4) {
                        gc.checkAPI('7', ()=>gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT4, gl.TEXTURE_2D, text4, 0));
                        if (text5) {
                            gc.checkAPI('8', ()=>gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT5, gl.TEXTURE_2D, text5, 0));
                            if (text6) {
                                gc.checkAPI('9', ()=>gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT6, gl.TEXTURE_2D, text6, 0));
                                gc.checkAPI('10', ()=>gl.drawBuffers([gl.COLOR_ATTACHMENT0, gl.COLOR_ATTACHMENT1, gl.COLOR_ATTACHMENT2, gl.COLOR_ATTACHMENT3, gl.COLOR_ATTACHMENT4, gl.COLOR_ATTACHMENT5, gl.COLOR_ATTACHMENT6]));
                            } else {
                                gc.checkAPI('11', ()=>gl.drawBuffers([gl.COLOR_ATTACHMENT0, gl.COLOR_ATTACHMENT1, gl.COLOR_ATTACHMENT2, gl.COLOR_ATTACHMENT3, gl.COLOR_ATTACHMENT4, gl.COLOR_ATTACHMENT5]));
                            }
                        } else {
                            gc.checkAPI('12', ()=>gl.drawBuffers([gl.COLOR_ATTACHMENT0, gl.COLOR_ATTACHMENT1, gl.COLOR_ATTACHMENT2, gl.COLOR_ATTACHMENT3, gl.COLOR_ATTACHMENT4]));
                        }
                    } else {
                        gc.checkAPI('13', ()=>gl.drawBuffers([gl.COLOR_ATTACHMENT0, gl.COLOR_ATTACHMENT1, gl.COLOR_ATTACHMENT2, gl.COLOR_ATTACHMENT3]));
                    }
                } else {
                    gc.checkAPI('14', ()=>gl.drawBuffers([gl.COLOR_ATTACHMENT0, gl.COLOR_ATTACHMENT1, gl.COLOR_ATTACHMENT2]));
                }
            } else {
                gc.checkAPI('15', ()=>gl.drawBuffers([gl.COLOR_ATTACHMENT0, gl.COLOR_ATTACHMENT1]));
            }
        } else {
            gc.checkAPI('16', ()=>gl.drawBuffers([gl.COLOR_ATTACHMENT0]));
        }

        return fb!;
    }

    static loadShader(gcFrom: GPUChecker, type: GLenum, source: string): WebGLShader {
        let gc = new GPUChecker(gcFrom.gl, gcFrom, 'loadShader');

        const shader = gc.checkAPI('createShader', ()=>gc.gl.createShader(type), true) as WebGLShader;
        
        // not checking errors here, done at higher level
        // Send the source to the shader object
        gc.gl.shaderSource(shader, source);
        // Compile the shader program
        gc.gl.compileShader(shader);

        return shader;
    }

    static allocateRenderBuffer(gcFrom: GPUChecker, tag: string, internalFormat: GLenum, width: number, height: number) : [WebGLRenderbuffer, WebGLFramebuffer, WebGLFramebuffer] {
        const gc = new GPUChecker(gcFrom.gl, gcFrom, 'allocateRenderBuffer');
        const gl = gc.gl;

        const rb = gc.checkAPI('1', ()=>gl.createRenderbuffer(), true) as WebGLRenderbuffer;
        (rb! as any).tag = `${tag}RB`;
        gc.checkAPI('2', ()=>gl.bindRenderbuffer(gl.RENDERBUFFER, rb));
        gc.checkAPI('3', ()=>gl.renderbufferStorage(gl.RENDERBUFFER, internalFormat, width, height));

        let fbR = gc.checkAPI('15', ()=>gl.createFramebuffer(), true) as WebGLFramebuffer;
        (fbR! as any).tag = `${tag}FBR`;
        gc.checkAPI('4', ()=>gl.bindFramebuffer(gl.READ_FRAMEBUFFER, fbR));
        gc.checkAPI('5', ()=>gl.framebufferRenderbuffer(gl.READ_FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.RENDERBUFFER, rb!));

        let fbW = gc.checkAPI('15', ()=>gl.createFramebuffer(), true) as WebGLFramebuffer;
        (fbW! as any).tag = `${tag}FBW`;
        gc.checkAPI('6', ()=>gl.bindFramebuffer(gl.READ_FRAMEBUFFER, fbW));
        gc.checkAPI('7', ()=>gl.framebufferRenderbuffer(gl.READ_FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.RENDERBUFFER, rb!));

        return [rb!, fbR!, fbW!];
    }
    static allocatePage(gcFrom: GPUChecker, page: Page) {
        const gc = new GPUChecker(gcFrom.gl, gcFrom, 'allocatePage');
        const gl = gc.gl;

        let rbfbrfbw = gc.check('1', ()=>GPURunner.allocateRenderBuffer(gc, 'pageVisibleLatentC', gl.RGBA16UI, page.cellsWidth, page.cellsHeight)) as [WebGLRenderbuffer, WebGLFramebuffer, WebGLFramebuffer];
        if (rbfbrfbw !== null) {
            page.buffVisibleLatentC = rbfbrfbw[0];
            page.visibleLatentCFramebufferR = rbfbrfbw[1];
            page.visibleLatentCFramebufferW = rbfbrfbw[2];
        }
        
        rbfbrfbw = gc.check('2', ()=>GPURunner.allocateRenderBuffer(gc, 'pageVisibleLatentM', gl.RGBA16UI, page.cellsWidth, page.cellsHeight)) as [WebGLRenderbuffer, WebGLFramebuffer, WebGLFramebuffer];;
        if (rbfbrfbw !== null) {
            page.buffVisibleLatentM = rbfbrfbw[0];
            page.visibleLatentMFramebufferR = rbfbrfbw[1];
            page.visibleLatentMFramebufferW = rbfbrfbw[2];
        }

        rbfbrfbw = gc.check('3', ()=>GPURunner.allocateRenderBuffer(gc, 'pageDryLatentC', gl.RGBA16UI, page.cellsWidth, page.cellsHeight)) as [WebGLRenderbuffer, WebGLFramebuffer, WebGLFramebuffer];;
        if (rbfbrfbw !== null) {
            page.buffDryLatentC = rbfbrfbw[0];
            page.dryLatentCFramebufferR = rbfbrfbw[1];
            page.dryLatentCFramebufferW = rbfbrfbw[2];
        }

        rbfbrfbw = gc.check('4', ()=>GPURunner.allocateRenderBuffer(gc, 'pageDryLatentM', gl.RGBA16UI, page.cellsWidth, page.cellsHeight)) as [WebGLRenderbuffer, WebGLFramebuffer, WebGLFramebuffer];;
        if (rbfbrfbw !== null) {
            page.buffDryLatentM = rbfbrfbw[0];
            page.dryLatentMFramebufferR = rbfbrfbw[1];
            page.dryLatentMFramebufferW = rbfbrfbw[2];
        }

        rbfbrfbw = gc.check('5', ()=>GPURunner.allocateRenderBuffer(gc, 'pageWetLatentC', gl.RGBA16UI, page.cellsWidth, page.cellsHeight)) as [WebGLRenderbuffer, WebGLFramebuffer, WebGLFramebuffer];;
        if (rbfbrfbw !== null) {
            page.buffWetLatentC = rbfbrfbw[0];
            page.wetLatentCFramebufferR = rbfbrfbw[1];
            page.wetLatentCFramebufferW = rbfbrfbw[2];
        }

        rbfbrfbw = gc.check('6', ()=>GPURunner.allocateRenderBuffer(gc, 'pageWetLatentM', gl.RGBA16UI, page.cellsWidth, page.cellsHeight)) as [WebGLRenderbuffer, WebGLFramebuffer, WebGLFramebuffer];;
        if (rbfbrfbw !== null) {
            page.buffWetLatentM = rbfbrfbw[0];
            page.wetLatentMFramebufferR = rbfbrfbw[1];
            page.wetLatentMFramebufferW = rbfbrfbw[2];
        }

        rbfbrfbw = gc.check('7', ()=>GPURunner.allocateRenderBuffer(gc, 'pageWetDepth', gl.R16UI, page.cellsWidth, page.cellsHeight)) as [WebGLRenderbuffer, WebGLFramebuffer, WebGLFramebuffer];;
        if (rbfbrfbw !== null) {
            page.buffWetDepth = rbfbrfbw[0];
            page.wetDepthFramebufferR = rbfbrfbw[1];
            page.wetDepthFramebufferW = rbfbrfbw[2];
        }

        rbfbrfbw = gc.check('8', ()=>GPURunner.allocateRenderBuffer(gc, 'pageDryDepth', gl.R16UI, page.cellsWidth, page.cellsHeight)) as [WebGLRenderbuffer, WebGLFramebuffer, WebGLFramebuffer];;
        if (rbfbrfbw !== null) {
            page.buffDryDepth = rbfbrfbw[0];
            page.dryDepthFramebufferR = rbfbrfbw[1];
            page.dryDepthFramebufferW = rbfbrfbw[2];
        }

        rbfbrfbw = gc.check('9', ()=>GPURunner.allocateRenderBuffer(gc, 'pageWetAge', gl.R8UI, page.cellsWidth, page.cellsHeight)) as [WebGLRenderbuffer, WebGLFramebuffer, WebGLFramebuffer];;
        if (rbfbrfbw !== null) {
            page.buffWetAge = rbfbrfbw[0];
            page.wetAgeFramebufferR = rbfbrfbw[1];
            page.wetAgeFramebufferW = rbfbrfbw[2];
        }

        gl.bindRenderbuffer(gl.RENDERBUFFER, null);
        gl.bindFramebuffer(gl.READ_FRAMEBUFFER, null);
        gl.bindFramebuffer(gl.DRAW_FRAMEBUFFER, null);

    }

    static getCurrentViewport() : [number, number] {
        if (!GPURunner.gl || !GPURunner.gl.canvas) {
            throw new Error('GPU not initialized');
        }
        return [GPURunner.gl.canvas.width, GPURunner.gl.canvas.height];
    }

    static initChangeTexture(gcFrom: GPUChecker, tag: string, pageFrame: WebGLFramebuffer | null, internalFormat: GLint, format: GLenum, type: GLenum,
            readX0: number, readY0: number, readX1: number, readY1: number, writeX0: number, writeY0: number, writeX1: number, writeY1: number) : WorkingItem {
        let width = readX1 - readX0;
        let height = readY1 - readY0;

        const gc = new GPUChecker(gcFrom.gl, gcFrom, 'initChangeTexture');
        const gl = gc.gl;
       
        let changeWI = gc.check('2', ()=>GPURunner.workingFrameFactory.getItem(gc, `${tag}FB`, width, height, [{tag:tag, internalFormat:internalFormat, format:format, type:type}])) as WorkingItem;

        if (pageFrame !== null) {
            gc.checkAPI('1', ()=> gl.bindFramebuffer(gl.READ_FRAMEBUFFER, pageFrame));
        }

        if (pageFrame !== null) {
            gc.checkAPI('4', ()=> gl.bindFramebuffer(gl.DRAW_FRAMEBUFFER, changeWI.fb));
            gc.checkAPI('6', ()=> gl.blitFramebuffer(readX0, readY0, readX1, readY1, writeX0, writeY0, writeX1, writeY1, gl.COLOR_BUFFER_BIT, gl.NEAREST));
        }

        return changeWI;
    }

    static initPageChangeWetDepthTextureFromPage(gcFrom: GPUChecker, page: Page, change: PageChange) {
        const gc = new GPUChecker(gcFrom.gl, gcFrom, 'initPageChangeWetDepthTextureFromPage');
        const gl = gc.gl;

        let pageReadX0 = change.pageX;
        let changeWriteX0 = 0;
        let pageReadX1 = change.pageX + change.width;
        let changeWriteX1 = change.width;

        let pageReadY0 = change.pageY;
        let changeWriteY0 = change.height;
        let pageReadY1 = change.pageY + change.height;
        let changeWriteY1 = 0;

        if (change.pageY === 0 && change.pageX === 0 && change.width < page.cellsWidth && change.height < page.cellsHeight) {
            // because on pixel phones this causes someflip?
            pageReadY0 ++;
            pageReadY1 ++;
            changeWriteY0 --;
            changeWriteY1 --;
        }

        // set up the wet depth texture and copy from the page
        let changeBufs = gc.check('1', ()=>GPURunner.initChangeTexture(gc, 'pageWetDepthTexture', page.wetDepthFramebufferR!, gl.R16UI, gl.RED_INTEGER, gl.UNSIGNED_SHORT, pageReadX0, pageReadY0, pageReadX1, pageReadY1, changeWriteX0, changeWriteY0, changeWriteX1, changeWriteY1)) as WorkingItem;
        change.pageWetDepthTexture = changeBufs.tex0;
        change.pageWetDepthFramebufferW = changeBufs.fb;

        changeBufs = gc.check('2', ()=>GPURunner.initChangeTexture(gc, 'pageDryDepthTexture', page.dryDepthFramebufferR!, gl.R16UI, gl.RED_INTEGER, gl.UNSIGNED_SHORT, pageReadX0, pageReadY0, pageReadX1, pageReadY1, changeWriteX0, changeWriteY0, changeWriteX1, changeWriteY1)) as WorkingItem;
        change.pageDryDepthTexture = changeBufs.tex0;
        change.pageDryDepthFramebufferW = changeBufs.fb;

    
        gl.bindFramebuffer(gl.READ_FRAMEBUFFER, null);
        gl.bindFramebuffer(gl.DRAW_FRAMEBUFFER, null);
    }
    static applyPageChangeWetDepthTextureToPage(gcFrom: GPUChecker, page: Page, change: PageChange) {
        const gc = new GPUChecker(gcFrom.gl, gcFrom, ' applyPageChangeWetDepthTextureToPage');
        const gl = gc.gl;

        let changeReadX0 = 0;
        let pageWriteX0 = change.pageX;
        let changeReadX1 = change.width;
        let pageWriteX1 = change.pageX + change.width;

        let changeReadY0 = change.height;
        let pageWriteY0 = change.pageY;
        let changeReadY1 = 0;
        let pageWriteY1 = change.pageY + change.height;

        let fbRead = gl.createFramebuffer();
        gc.checkAPI('1', ()=>gl.bindFramebuffer(gl.DRAW_FRAMEBUFFER, page.wetDepthFramebufferW));
        gc.checkAPI('2', ()=>gl.bindFramebuffer(gl.READ_FRAMEBUFFER, fbRead));
        gc.checkAPI('3', ()=>gl.framebufferTexture2D(gl.READ_FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, change.pageWetDepthTexture, 0));
        gc.checkAPI('4', ()=>gl.blitFramebuffer(changeReadX0, changeReadY0, changeReadX1, changeReadY1, pageWriteX0, pageWriteY0, pageWriteX1, pageWriteY1, gl.COLOR_BUFFER_BIT, gl.NEAREST));

        gc.checkAPI('5', ()=>gl.bindFramebuffer(gl.DRAW_FRAMEBUFFER, page.dryDepthFramebufferW));
        gc.checkAPI('6', ()=>gl.bindFramebuffer(gl.READ_FRAMEBUFFER, fbRead));
        gc.checkAPI('7', ()=>gl.framebufferTexture2D(gl.READ_FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, change.pageDryDepthTexture, 0));
        gc.checkAPI('8', ()=>gl.blitFramebuffer(changeReadX0, changeReadY0, changeReadX1, changeReadY1, pageWriteX0, pageWriteY0, pageWriteX1, pageWriteY1, gl.COLOR_BUFFER_BIT, gl.NEAREST));

        gl.deleteFramebuffer(fbRead);

    }

    static initPageChangeWetLatentTextureFromPage(gcFrom: GPUChecker, page: Page, change: PageChange) {
        const gc = new GPUChecker(gcFrom.gl, gcFrom, 'initPageChangeWetLatentTextureFromPage');
        const gl = gc.gl;

        let pageReadX0 = change.pageX;
        let changeWriteX0 = 0;
        let pageReadX1 = change.pageX + change.width;
        let changeWriteX1 = change.width;

        let pageReadY0 = change.pageY;
        let changeWriteY0 = change.height;
        let pageReadY1 = change.pageY + change.height;
        let changeWriteY1 = 0;

        if (change.pageY === 0 && change.pageX === 0 && change.width < page.cellsWidth && change.height < page.cellsHeight) {
            // because on pixel phones this causes someflip?
            pageReadY0 ++;
            pageReadY1 ++;
            changeWriteY0 --;
            changeWriteY1 --;
        }
       
        // set up the wet color and depth textures and copy from the page
        let changeBufs = gc.check('1', ()=>GPURunner.initChangeTexture(gc, 'pageWetLatentCTexture', page.wetLatentCFramebufferR!, gl.RGBA16UI, gl.RGBA_INTEGER, gl.UNSIGNED_SHORT, pageReadX0, pageReadY0, pageReadX1, pageReadY1, changeWriteX0, changeWriteY0, changeWriteX1, changeWriteY1)) as WorkingItem;
        change.pageWetLatentCTexture = changeBufs.tex0;
        change.pageWetLatentCFramebufferW = changeBufs.fb;

        changeBufs = gc.check('2', ()=>GPURunner.initChangeTexture(gc, 'pageWetLatentMTexture', page.wetLatentMFramebufferR!, gl.RGBA16UI, gl.RGBA_INTEGER, gl.UNSIGNED_SHORT, pageReadX0, pageReadY0, pageReadX1, pageReadY1, changeWriteX0, changeWriteY0, changeWriteX1, changeWriteY1)) as WorkingItem;
        change.pageWetLatentMTexture = changeBufs.tex0;
        change.pageWetLatentMFramebufferW = changeBufs.fb;

        gl.bindFramebuffer(gl.READ_FRAMEBUFFER, null);
        gl.bindFramebuffer(gl.DRAW_FRAMEBUFFER, null);
    }

    static applyPageChangeWetLatentTextureToPage(gcFrom: GPUChecker, page: Page, change: PageChange) {
        const gc = new GPUChecker(gcFrom.gl, gcFrom, 'applyPageChangeWetLatentTextureToPage');
        const gl = gc.gl;

        let changeReadX0 = 0;
        let pageWriteX0 = change.pageX;
        let changeReadX1 = change.width;
        let pageWriteX1 = change.pageX + change.width;

        let changeReadY0 = change.height;
        let pageWriteY0 = change.pageY;
        let changeReadY1 = 0;
        let pageWriteY1 = change.pageY + change.height;

        gc.checkAPI('1', ()=>gl.bindFramebuffer(gl.DRAW_FRAMEBUFFER, page.wetLatentCFramebufferW));
        if (GPUChecker.errorCode !== 0) {
            GPUChecker.errorText = 'why here'
        }
        let fbRead = gc.checkAPI('2', ()=>gl.createFramebuffer(), true) as WebGLFramebuffer;
        gc.checkAPI('3', ()=>gl.bindFramebuffer(gl.READ_FRAMEBUFFER, fbRead));
        gc.checkAPI('4', ()=>gl.framebufferTexture2D(gl.READ_FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, change.pageWetLatentCTexture, 0));
        gc.checkAPI('5', ()=>gl.blitFramebuffer(changeReadX0, changeReadY0, changeReadX1, changeReadY1, pageWriteX0, pageWriteY0, pageWriteX1, pageWriteY1, gl.COLOR_BUFFER_BIT, gl.NEAREST));

        gc.checkAPI('6', ()=>gl.bindFramebuffer(gl.DRAW_FRAMEBUFFER, page.wetLatentMFramebufferW));
        gc.checkAPI('7', ()=>gl.bindFramebuffer(gl.READ_FRAMEBUFFER, fbRead));
        gc.checkAPI('8', ()=>gl.framebufferTexture2D(gl.READ_FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, change.pageWetLatentMTexture, 0));
        gc.checkAPI('9', ()=>gl.blitFramebuffer(changeReadX0, changeReadY0, changeReadX1, changeReadY1, pageWriteX0, pageWriteY0, pageWriteX1, pageWriteY1, gl.COLOR_BUFFER_BIT, gl.NEAREST));

        gl.deleteFramebuffer(fbRead);
    }

    static initPageChangeWetAgeTextureFromPage(gcFrom: GPUChecker, page: Page, change: PageChange) {
        const gc = new GPUChecker(gcFrom.gl, gcFrom, 'initPageChangeWetAgeTextureFromPage');
        const gl = gc.gl;

        let pageReadX0 = change.pageX;
        let changeWriteX0 = 0;
        let pageReadX1 = change.pageX + change.width;
        let changeWriteX1 = change.width;

        let pageReadY0 = change.pageY;
        let changeWriteY0 = change.height;
        let pageReadY1 = change.pageY + change.height;
        let changeWriteY1 = 0;

        if (change.pageY === 0 && change.pageX === 0 && change.width < page.cellsWidth && change.height < page.cellsHeight) {
            // because on pixel phones this causes someflip?
            pageReadY0 ++;
            pageReadY1 ++;
            changeWriteY0 --;
            changeWriteY1 --;
        }

        // setup the wet drying time mask
        let changeBufs = gc.check('1', ()=>GPURunner.initChangeTexture(gc, 'pageWetAgeTexture', page.wetAgeFramebufferR!, gl.R8UI, gl.RED_INTEGER, gl.UNSIGNED_BYTE, pageReadX0, pageReadY0, pageReadX1, pageReadY1, changeWriteX0, changeWriteY0, changeWriteX1, changeWriteY1)) as WorkingItem;
        change.pageWetAgeTexture = changeBufs.tex0;
        change.pageWetAgeFramebufferW = changeBufs.fb;
        
        gl.bindFramebuffer(gl.READ_FRAMEBUFFER, null);
        gl.bindFramebuffer(gl.DRAW_FRAMEBUFFER, null);
    }
    static applyPageChangeWetAgeTextureToPage(gcFrom: GPUChecker, page: Page, change: PageChange) {
        const gc = new GPUChecker(gcFrom.gl, gcFrom, 'applyPageChangeWetAgeTextureToPage');
        const gl = gc.gl;

        let changeReadX0 = 0;
        let pageWriteX0 = change.pageX;
        let changeReadX1 = change.width;
        let pageWriteX1 = change.pageX + change.width;

        let changeReadY0 = change.height;
        let pageWriteY0 = change.pageY;
        let changeReadY1 = 0;
        let pageWriteY1 = change.pageY + change.height;

        let fbRead = gc.checkAPI('1', ()=>gl.createFramebuffer(), true) as WebGLFramebuffer;

        gc.checkAPI('2', ()=>gl.bindFramebuffer(gl.DRAW_FRAMEBUFFER, page.wetAgeFramebufferW));
        gc.checkAPI('3', ()=>gl.bindFramebuffer(gl.READ_FRAMEBUFFER, fbRead));
        gc.checkAPI('4', ()=>gl.framebufferTexture2D(gl.READ_FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, change.pageWetAgeTexture, 0));
        gc.checkAPI('5', ()=>gl.blitFramebuffer(changeReadX0, changeReadY0, changeReadX1, changeReadY1, pageWriteX0, pageWriteY0, pageWriteX1, pageWriteY1, gl.COLOR_BUFFER_BIT, gl.NEAREST));

        gc.checkAPI('6', ()=>gl.bindFramebuffer(gl.READ_FRAMEBUFFER, null));
        gc.checkAPI('7', ()=>gl.bindFramebuffer(gl.DRAW_FRAMEBUFFER, null));
        gc.checkAPI('8', ()=>gl.deleteFramebuffer(fbRead));

    }


    static initPageChangeVisibleLatentTextureFromPage(gcFrom: GPUChecker, page: Page, change: PageChange) {
        const gc = new GPUChecker(gcFrom.gl, gcFrom, 'initPageChangeVisibleLatentTextureFromPage');
        const gl = gc.gl;

        let pageReadX0 = change.pageX;
        let changeWriteX0 = 0;
        let pageReadX1 = change.pageX + change.width;
        let changeWriteX1 = change.width;

        let pageReadY0 = change.pageY;
        let changeWriteY0 = change.height;
        let pageReadY1 = change.pageY + change.height;
        let changeWriteY1 = 0;

        if (change.pageY === 0 && change.pageX === 0 && change.width < page.cellsWidth && change.height < page.cellsHeight) {
            // because on pixel phones this causes someflip?
            pageReadY0 ++;
            pageReadY1 ++;
            changeWriteY0 --;
            changeWriteY1 --;
        }
        // the visible color texture
        let changeBufs = gc.check('1', ()=>GPURunner.initChangeTexture(gc, 'pageVisibleLatentCTexture', page.visibleLatentCFramebufferR!, gl.RGBA16UI, gl.RGBA_INTEGER, gl.UNSIGNED_SHORT, pageReadX0, pageReadY0, pageReadX1, pageReadY1, changeWriteX0, changeWriteY0, changeWriteX1, changeWriteY1)) as WorkingItem;
        change.pageVisibleLatentCTexture = changeBufs.tex0;
        change.pageVisibleLatentCFramebufferW = changeBufs.fb;

        changeBufs = gc.check('2', ()=>GPURunner.initChangeTexture(gc, 'pageVisibleLatentMTexture', page.visibleLatentMFramebufferR!, gl.RGBA16UI, gl.RGBA_INTEGER, gl.UNSIGNED_SHORT, pageReadX0, pageReadY0, pageReadX1, pageReadY1, changeWriteX0, changeWriteY0, changeWriteX1, changeWriteY1)) as WorkingItem;
        change.pageVisibleLatentMTexture = changeBufs.tex0;
        change.pageVisibleLatentMFramebufferW = changeBufs.fb;

        gl.bindFramebuffer(gl.READ_FRAMEBUFFER, null);
        gl.bindFramebuffer(gl.DRAW_FRAMEBUFFER, null);
    }
    static applyPageChangeVisibleLatentTextureToPage(gcFrom: GPUChecker, page: Page, change: PageChange) {
        const gc = new GPUChecker(gcFrom.gl, gcFrom, 'applyPageChangeVisibleLatentTextureToPage');
        const gl = gc.gl;

        let changeReadX0 = 0;
        let pageWriteX0 = change.pageX;
        let changeReadX1 = change.width;
        let pageWriteX1 = change.pageX + change.width;

        let changeReadY0 = change.height;
        let pageWriteY0 = change.pageY;
        let changeReadY1 = 0;
        let pageWriteY1 = change.pageY + change.height;

        let fbRead = gc.checkAPI('0', ()=>gl.createFramebuffer(), true) as WebGLFramebuffer;

        gc.checkAPI('1', ()=>gl.bindFramebuffer(gl.DRAW_FRAMEBUFFER, page.visibleLatentCFramebufferW));
        gc.checkAPI('2', ()=>gl.bindFramebuffer(gl.READ_FRAMEBUFFER, fbRead));
        gc.checkAPI('3', ()=>gl.framebufferTexture2D(gl.READ_FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, change.pageVisibleLatentCTexture, 0));
        gc.checkAPI('4', ()=>gl.blitFramebuffer(changeReadX0, changeReadY0, changeReadX1, changeReadY1, pageWriteX0, pageWriteY0, pageWriteX1, pageWriteY1, gl.COLOR_BUFFER_BIT, gl.NEAREST));

        gc.checkAPI('5', ()=>gl.bindFramebuffer(gl.DRAW_FRAMEBUFFER, page.visibleLatentMFramebufferW));
        gc.checkAPI('6', ()=>gl.bindFramebuffer(gl.READ_FRAMEBUFFER, fbRead));
        gc.checkAPI('7', ()=>gl.framebufferTexture2D(gl.READ_FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, change.pageVisibleLatentMTexture, 0));
        gc.checkAPI('8', ()=>gl.blitFramebuffer(changeReadX0, changeReadY0, changeReadX1, changeReadY1, pageWriteX0, pageWriteY0, pageWriteX1, pageWriteY1, gl.COLOR_BUFFER_BIT, gl.NEAREST));

        gl.bindFramebuffer(gl.READ_FRAMEBUFFER, null);
        gl.bindFramebuffer(gl.DRAW_FRAMEBUFFER, null);
        gl.deleteFramebuffer(fbRead);

    }

    static initPageChangeDryLatentTextureFromPage(gcFrom: GPUChecker, page: Page, change: PageChange) {
        const gc = new GPUChecker(gcFrom.gl, gcFrom, 'initPageChangeDryLatentTextureFromPage');
        const gl = gc.gl;

        let pageReadX0 = change.pageX;
        let changeWriteX0 = 0;
        let pageReadX1 = change.pageX + change.width;
        let changeWriteX1 = change.width;

        let pageReadY0 = change.pageY;
        let changeWriteY0 = change.height;
        let pageReadY1 = change.pageY + change.height;
        let changeWriteY1 = 0;

        if (change.pageY === 0 && change.pageX === 0 && change.width < page.cellsWidth && change.height < page.cellsHeight) {
            // because on pixel phones this causes someflip?
            pageReadY0 ++;
            pageReadY1 ++;
            changeWriteY0 --;
            changeWriteY1 --;
        }

        // and the dry color texture
        let changeBufs = gc.check('1', ()=>GPURunner.initChangeTexture(gc, 'pageDryLatentCTexture', page.dryLatentCFramebufferR!, gl.RGBA16UI, gl.RGBA_INTEGER, gl.UNSIGNED_SHORT, pageReadX0, pageReadY0, pageReadX1, pageReadY1, changeWriteX0, changeWriteY0, changeWriteX1, changeWriteY1)) as WorkingItem;
        change.pageDryLatentCTexture = changeBufs.tex0;
        change.pageDryLatentCFramebufferW = changeBufs.fb;

        changeBufs = gc.check('2', ()=>GPURunner.initChangeTexture(gc, 'pageDryLatentMTexture', page.dryLatentMFramebufferR!, gl.RGBA16UI, gl.RGBA_INTEGER, gl.UNSIGNED_SHORT, pageReadX0, pageReadY0, pageReadX1, pageReadY1, changeWriteX0, changeWriteY0, changeWriteX1, changeWriteY1)) as WorkingItem;
        change.pageDryLatentMTexture = changeBufs.tex0;
        change.pageDryLatentMFramebufferW = changeBufs.fb;

        gl.bindFramebuffer(gl.READ_FRAMEBUFFER, null);
        gl.bindFramebuffer(gl.DRAW_FRAMEBUFFER, null);
    }

    static applyPageChangeDryLatentTextureToPage(gcFrom: GPUChecker, page: Page, change: PageChange) {
        const gc = new GPUChecker(gcFrom.gl, gcFrom, 'applyPageChangeDryLatentTextureToPage');
        const gl = gc.gl;

        let changeReadX0 = 0;
        let pageWriteX0 = change.pageX;
        let changeReadX1 = change.width;
        let pageWriteX1 = change.pageX + change.width;

        let changeReadY0 = change.height;
        let pageWriteY0 = change.pageY;
        let changeReadY1 = 0;
        let pageWriteY1 = change.pageY + change.height;

        let fbRead = gc.checkAPI('0', ()=>gl.createFramebuffer(), true) as WebGLFramebuffer;

        gc.checkAPI('1', ()=>gl.bindFramebuffer(gl.DRAW_FRAMEBUFFER, page.dryLatentCFramebufferW));
        gc.checkAPI('2', ()=>gl.bindFramebuffer(gl.READ_FRAMEBUFFER, fbRead));
        gc.checkAPI('3', ()=>gl.framebufferTexture2D(gl.READ_FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, change.pageDryLatentCTexture, 0));
        gc.checkAPI('4', ()=>gl.blitFramebuffer(changeReadX0, changeReadY0, changeReadX1, changeReadY1, pageWriteX0, pageWriteY0, pageWriteX1, pageWriteY1, gl.COLOR_BUFFER_BIT, gl.NEAREST));

        gc.checkAPI('5', ()=>gl.bindFramebuffer(gl.DRAW_FRAMEBUFFER, page.dryLatentMFramebufferW));
        gc.checkAPI('6', ()=>gl.bindFramebuffer(gl.READ_FRAMEBUFFER, fbRead));
        gc.checkAPI('7', ()=>gl.framebufferTexture2D(gl.READ_FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, change.pageDryLatentMTexture, 0));
        gc.checkAPI('8', ()=>gl.blitFramebuffer(changeReadX0, changeReadY0, changeReadX1, changeReadY1, pageWriteX0, pageWriteY0, pageWriteX1, pageWriteY1, gl.COLOR_BUFFER_BIT, gl.NEAREST));

        gl.bindFramebuffer(gl.READ_FRAMEBUFFER, null);
        gl.bindFramebuffer(gl.DRAW_FRAMEBUFFER, null);
        gl.deleteFramebuffer(fbRead);

    }



    static initTouchWetPageChange(gcFrom: GPUChecker, page: Page, change: TouchPageChange) {
        const gc = new GPUChecker(gcFrom.gl, gcFrom, 'initTouchWetPageChange');
        const gl = gc.gl;
        gc.check('1', ()=>GPURunner.initPageChangeWetAgeTextureFromPage(gc, page, change));
        gc.check('2', ()=>GPURunner.initPageChangeWetLatentTextureFromPage(gc, page, change));
        gc.check('3', ()=>GPURunner.initPageChangeWetDepthTextureFromPage(gc, page, change));
    }

    static initTouchVisiblePageChange(gcFrom: GPUChecker, page: Page, change: TouchPageChange) {
        const gc = new GPUChecker(gcFrom.gl, gcFrom, 'initTouchVisiblePageChange');
        const gl = gc.gl;
        gc.check('1', ()=>GPURunner.initPageChangeDryLatentTextureFromPage(gc, page, change));
        gc.check('2', ()=>GPURunner.initPageChangeVisibleLatentTextureFromPage(gc, page, change));
    }


    static applyTouchWetPageChange(gcFrom: GPUChecker, page: Page, change: TouchPageChange) {
        const gc = new GPUChecker(gcFrom.gl, gcFrom, 'applyTouchWetPageChange');
        const gl = gc.gl;
        gc.check('1', ()=>GPURunner.applyPageChangeWetLatentTextureToPage(gc, page, change));
        gc.check('2', ()=>GPURunner.applyPageChangeWetDepthTextureToPage(gc, page, change));
    }

    static applyTouchVisiblePageChange(gcFrom: GPUChecker, page: Page, change: TouchPageChange) {
        const gc = new GPUChecker(gcFrom.gl, gcFrom, 'applyTouchVisiblePageChange');
        const gl = gc.gl;
        gc.check('1', ()=>GPURunner.applyPageChangeWetAgeTextureToPage(gc, page, change));
        gc.check('2', ()=>GPURunner.applyPageChangeVisibleLatentTextureToPage(gc, page, change));
    }

    static initDryPageChange(gcFrom: GPUChecker, page: Page, change: DryPageChange) {
        const gc = new GPUChecker(gcFrom.gl, gcFrom, 'initDryPageChange');
        const gl = gc.gl;
        gc.check('1', ()=>GPURunner.initPageChangeWetDepthTextureFromPage(gc, page, change));
        gc.check('2', ()=>GPURunner.initPageChangeVisibleLatentTextureFromPage(gc, page, change));
        gc.check('3', ()=>GPURunner.initPageChangeWetLatentTextureFromPage(gc, page, change));
        gc.check('4', ()=>GPURunner.initPageChangeDryLatentTextureFromPage(gc, page, change));
        gc.check('5', ()=>GPURunner.initPageChangeWetAgeTextureFromPage(gc, page, change));
    }

    static applyDryPageChange(gcFrom: GPUChecker, page: Page, change: DryPageChange) {
        const gc = new GPUChecker(gcFrom.gl, gcFrom, 'applyDryPageChange');
        const gl = gc.gl;
        gc.check('1', ()=>GPURunner.applyPageChangeWetDepthTextureToPage(gc, page, change));
        gc.check('2', ()=>GPURunner.applyPageChangeWetAgeTextureToPage(gc, page, change));
        gc.check('3', ()=>GPURunner.applyPageChangeWetLatentTextureToPage(gc, page, change));
        gc.check('4', ()=>GPURunner.applyPageChangeDryLatentTextureToPage(gc, page, change));
    }

    static initRefreshPageChange(gcFrom: GPUChecker, page: Page, change: RefreshPageChange) {
        const gc = new GPUChecker(gcFrom.gl, gcFrom, 'initRefreshPageChange');
        const gl = gc.gl;
        gc.check('1', ()=>GPURunner.initPageChangeVisibleLatentTextureFromPage(gc, page, change));
        gc.check('2', ()=>GPURunner.initPageChangeWetDepthTextureFromPage(gc, page, change));
        gc.check('3', ()=>GPURunner.initPageChangeWetAgeTextureFromPage(gc, page, change));
    }

    static initSamplePageChange(gcFrom: GPUChecker, page: Page, change: TouchPageChange) {
        const gc = new GPUChecker(gcFrom.gl, gcFrom, 'initSamplePageChange');
        const gl = gc.gl;
        gc.check('1', ()=>GPURunner.initPageChangeWetDepthTextureFromPage(gc, page, change));
        gc.check('2', ()=>GPURunner.initPageChangeVisibleLatentTextureFromPage(gc, page, change));
    }

    static debugGPUGetBuffer(pageFB: WebGLFramebuffer, intoBuff: Uint16Array, internalFormat: GLint, format: GLenum, readX0: number, readY0: number, readX1: number, readY1: number)  {
        let gl = GPURunner.gl!;
        const gc = new GPUChecker(gl, undefined, 'debug');

        let width = readX1 - readX0;
        let height = readY1 - readY0;
        let writeX0 = 0;
        let writeY0 = 0;
        let writeX1 = width;
        let writeY1 = height;
        // blit the wet depth buffer to a texture and then readpixels
        let changeBufs = GPURunner.initChangeTexture(gc, 'debug', pageFB!, internalFormat, format, gl.UNSIGNED_SHORT, readX0, readY0, readX1, readY1, writeX0, writeY0, writeX1, writeY1);
        let changeTexture = changeBufs.tex0;
        let fbTemp = gl.createFramebuffer();
        gl.bindFramebuffer(gl.READ_FRAMEBUFFER, fbTemp);
        gl.framebufferTexture2D(gl.READ_FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, changeTexture, 0);
        gl.readPixels(0, 0, width, height, format, gl.UNSIGNED_SHORT, intoBuff);
    }

    static debugGPUGetDepth(page: Page, readX0: number, readY0: number, readX1: number, readY1: number): Uint16Array {
        let gl = GPURunner.gl!;
        let width = readX1 - readX0;
        let height = readY1 - readY0;
        let buf = new Uint16Array(width * height);
        GPURunner.debugGPUGetBuffer(page.wetDepthFramebufferR!, buf, gl.R16UI, gl.RED_INTEGER, readX0, readY0, readX1, readY1);
        return buf;
    }
    static debugGPUGetLatent(pageFB: WebGLFramebuffer, readX0: number, readY0: number, readX1: number, readY1: number): Uint16Array {
        let gl = GPURunner.gl!;
        let width = readX1 - readX0;
        let height = readY1 - readY0;
        let buf = new Uint16Array(width * height * 4);
        GPURunner.debugGPUGetBuffer(pageFB, buf, gl.RGBA16UI, gl.RGBA_INTEGER, readX0, readY0, readX1, readY1);
        return buf;
    }

    static clearPage(page: Page) {

        if (!GPURunner.gl) {
            throw new Error('GPU not initialized');
        }
        let gl = GPURunner.gl;

        const fb = gl.createFramebuffer();
        gl.bindFramebuffer(gl.FRAMEBUFFER, fb);
        gl.framebufferRenderbuffer(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.RENDERBUFFER, page.buffVisibleLatentC!);
        gl.clearBufferuiv(gl.COLOR, 0, new Uint16Array([0, 0, 0, 65535])); // white is max C3 + a bit of missing rgb
        gl.framebufferRenderbuffer(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.RENDERBUFFER, page.buffVisibleLatentM!);
        gl.clearBufferuiv(gl.COLOR, 0, new Uint16Array([43000, 33000, 27000, 0]));
        
        gl.framebufferRenderbuffer(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.RENDERBUFFER, page.buffDryLatentC!);
        gl.clearBufferuiv(gl.COLOR, 0, new Uint16Array([0, 0, 0, 65535]));
        gl.framebufferRenderbuffer(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.RENDERBUFFER, page.buffDryLatentM!);
        gl.clearBufferuiv(gl.COLOR, 0, new Uint16Array([43000, 33000, 27000, 0]));

        gl.framebufferRenderbuffer(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.RENDERBUFFER, page.buffWetLatentC!);
        gl.clearBufferuiv(gl.COLOR, 0, new Uint16Array([0,0,0,0]));
        gl.framebufferRenderbuffer(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.RENDERBUFFER, page.buffWetLatentM!);
        gl.clearBufferuiv(gl.COLOR, 0, new Uint16Array([0,0,0,1]));

        gl.framebufferRenderbuffer(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.RENDERBUFFER, page.buffWetDepth!);
        gl.clearBufferuiv(gl.COLOR, 0, new Uint16Array([0,0,0,0]));

        gl.framebufferRenderbuffer(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.RENDERBUFFER, page.buffDryDepth!);
        gl.clearBufferuiv(gl.COLOR, 0, new Uint16Array([0,0,0,0]));

        gl.framebufferRenderbuffer(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.RENDERBUFFER, page.buffWetAge!);
        gl.clearBufferuiv(gl.COLOR, 0, new Uint8Array([0,0,0,0]));

        gl.deleteFramebuffer(fb);

    }


}

